import * as React from 'react';
import styled from 'styled-components';

import logo from '../resources/logo.png';

const AuthLayout = ({
  children,
  mainText,
  companyText,
  secondaryText,
}: AuthLayoutProps) => {
  return (
    <Root>
      <LeftPanel>
        <BrandContent>
          <Tagline>Exceptional service for all your permitting needs</Tagline>
          <Mission>
            Making the permitting experience the easiest part of your construction project.
          </Mission>
        </BrandContent>
        <Copyright>&copy; {new Date().getFullYear()} Elite Permits</Copyright>
      </LeftPanel>
      <RightPanel>
        <FormCard>
          <LogoImg src={logo} alt="Elite Permits" />
          <MainText>{mainText}</MainText>
          <CompanyText>{companyText}</CompanyText>
          {secondaryText && (
            <SecondaryText>{secondaryText}</SecondaryText>
          )}
          {children}
        </FormCard>
      </RightPanel>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  min-height: 100vh;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--space-10);
  background: linear-gradient(
    135deg,
    var(--primary-800) 0%,
    var(--primary-600) 100%
  );
  color: var(--neutral-0);
  position: relative;

  @media (max-width: 900px) {
    display: none;
  }
`;

const BrandContent = styled.div`
  max-width: 420px;
  text-align: left;
`;

const Tagline = styled.h1`
  font-size: var(--fs-4xl);
  font-weight: var(--fw-bold);
  line-height: var(--lh-tight);
  margin: 0 0 var(--space-6);
  color: var(--neutral-0);
  letter-spacing: -0.02em;
`;

const Mission = styled.p`
  font-size: var(--fs-lg);
  line-height: var(--lh-relaxed);
  color: var(--primary-200);
  margin: 0;
`;

const Copyright = styled.p`
  position: absolute;
  bottom: var(--space-6);
  font-size: var(--fs-sm);
  color: var(--primary-300);
  margin: 0;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--space-8);
  background: var(--app-bg, var(--neutral-50));
  overflow-y: auto;
  min-height: 100vh;
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: var(--space-6);
`;

const MainText = styled.h1`
  color: var(--neutral-900);
  font-size: var(--fs-3xl);
  font-weight: var(--fw-bold);
  margin: 0;
  text-align: center;
  letter-spacing: -0.01em;
`;

const CompanyText = styled.h2`
  color: var(--primary-600);
  font-size: var(--fs-lg);
  font-weight: var(--fw-medium);
  margin: var(--space-1) 0 0;
  text-align: center;
`;

const SecondaryText = styled.p`
  color: var(--neutral-500);
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  margin: var(--space-4) 0 0;
  text-align: center;
`;

interface AuthLayoutProps {
  children: React.ReactNode | React.ReactNodeArray;
  mainText: string;
  companyText: string;
  secondaryText?: string;
}

export default AuthLayout;
